/**
 * Registration plates are typed by hand — in a vCard, in the car config, in a
 * fleet reminder — so "WA 12345", "WA-12345" and "wa12345" all name the same
 * truck. Every comparison between two plates goes through here, or the same
 * pair ends up equal in one screen and different in the next.
 */
export const normalizePlate = (plates: string): string =>
  plates.replace(/[^a-z0-9]/gi, "").toUpperCase()

/**
 * A car's label with its own plate dropped, for screens that want the fleet's
 * name for the vehicle rather than its registration. GPS providers name cars
 * like "47 SPRINTER (ST0833M)" — side number, model, then the plate in
 * brackets — and the plate is already shown elsewhere or not wanted at all.
 * Only a trailing bracket naming this car's plate is cut, so a label that
 * merely ends in a bracket keeps it.
 */
export const carLabelWithoutPlate = (
  label: string,
  plates: string,
): string | undefined => {
  const trailingBracket = /\s*\(([^()]*)\)\s*$/.exec(label)
  const withoutPlate =
    trailingBracket &&
    plates &&
    normalizePlate(trailingBracket[1]) === normalizePlate(plates)
      ? label.slice(0, trailingBracket.index)
      : label
  return withoutPlate.trim() || undefined
}

/**
 * What a chat name carries in front of these plates, or "". Dispatchers put
 * the truck's side number there ("05 WM2456S Daniel Marcian" -> "05"); it
 * belongs to the truck, so a driver rotation that rebuilds the name keeps it.
 * Plates match however they are spaced and may carry a trailer after a slash
 * ("WM2456S/NCZ1"). A name without these plates has no prefix. Same rule as
 * account_config's `prefix_before_plates`, which renames on the backend.
 */
export const chatNamePrefix = (name: string, plates: string): string => {
  const target = normalizePlate(plates)
  if (!target) return ""
  const tokens = name.split(/\s+/).filter(Boolean)
  for (let start = 0; start < tokens.length; start++) {
    let joined = ""
    for (let end = start; end < tokens.length; end++) {
      const [head, ...trailer] = tokens[end].split("/")
      joined += normalizePlate(head)
      if (joined === target) return tokens.slice(0, start).join(" ")
      if (trailer.length > 0 || !target.startsWith(joined)) break
    }
  }
  return ""
}
